import { createContext, useContext } from 'react';

/**
 * Where "the top" is, now that the page itself does not scroll.
 *
 * The shell pins the rail and the top navigation and hands the main area a scrolling region of
 * its own, so `window.scrollTo` addresses a document that never moves. A view that replaces what
 * it is showing — opening a tournament, changing page, shrinking the page size — has to reset
 * *that* element, and only the shell knows which one it is. So the shell publishes the reset and
 * the views call it.
 *
 * A view is otherwise left holding a scroll position from taller content: the new screen renders
 * above the viewport and the operator is looking at the blank space under it, which reads as an
 * empty section rather than as a scroll position.
 */

const MainScrollContext = createContext<() => void>(() => {});

export const MainScrollProvider = MainScrollContext.Provider;

/**
 * Brings an element into view by moving the shell's `<main>`, and nothing else (periods A6b, A6c): the Change link's
 * reveal of the Periods card. `scrollIntoView` would scroll every scrollable ancestor, the window included.
 */
const MainRevealContext = createContext<(element: HTMLElement, reducedMotion: boolean) => void>(() => {});

export const MainRevealProvider = MainRevealContext.Provider;

export function useMainReveal(): (element: HTMLElement, reducedMotion: boolean) => void {
  return useContext(MainRevealContext);
}

/** Scrolls the main region back to the top. A no-op outside the shell. */
export function useMainScroll(): () => void {
  return useContext(MainScrollContext);
}
